import React, { useState } from "react";

// Cache guessed domain per payee within the session so we don't repeatedly
// probe brands that have no logo. Persisted in sessionStorage keyed by payee
// so repeat renders (e.g. re-open the Register) skip the img fetch entirely.
const CACHE_KEY = "sb_merchant_logo_v1";
const _mem = (() => {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
})();
function _persist() {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(_mem)); } catch {}
}

/** Guess a plausible domain for a merchant name.
 *  "Starbucks" → "starbucks.com"; "Chipotle Mexican Grill" → "chipotle.com".
 *  Returns null when the guess is nonsense (numeric-only, single letter, etc). */
export function guessDomain(payeeRaw) {
  if (!payeeRaw) return null;
  const p = String(payeeRaw).toLowerCase().trim();
  // First token (before space, dash, comma) captures most brands.
  const first = p.split(/[\s,\-–—/·]+/)[0] || "";
  const clean = first.replace(/[^a-z0-9]/g, "");
  if (clean.length < 3) return null;
  return `${clean}.com`;
}

/** Fixed color per initial so fallbacks are stable per merchant. */
const PALETTE = ["#fdecc8", "#c8e7fd", "#f4c2c2", "#d9d4f1", "#b5e4ca", "#ffc8dd", "#fdd692", "#ffd6a5"];
function initialColor(letter) {
  const c = (letter || "?").toUpperCase().charCodeAt(0);
  return PALETTE[c % PALETTE.length];
}

/** <MerchantLogo payee="Starbucks" size={24} /> — tries clearbit logo, falls
 *  back to a colored initial circle. Zero-cost when there's no payee. */
export default function MerchantLogo({ payee, size = 24 }) {
  const domain = guessDomain(payee);
  const cached = domain ? _mem[domain] : null;
  const [failed, setFailed] = useState(cached === "fail");
  const letter = (payee || "?").trim().charAt(0).toUpperCase() || "?";
  const bg = initialColor(letter);

  const showFallback = !domain || failed;
  if (showFallback) {
    return (
      <div
        data-testid="merchant-logo-fallback"
        className="rounded-full border-2 border-[#1a1a1a] flex items-center justify-center font-display font-black shrink-0"
        style={{
          width: size,
          height: size,
          background: bg,
          fontSize: Math.max(10, size * 0.5),
          color: "#1a1a1a",
        }}
        aria-hidden
      >
        {letter}
      </div>
    );
  }
  return (
    <img
      data-testid="merchant-logo-img"
      src={`https://logo.clearbit.com/${domain}`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onLoad={() => { _mem[domain] = "ok"; _persist(); }}
      onError={() => { _mem[domain] = "fail"; _persist(); setFailed(true); }}
      className="rounded-full border-2 border-[#1a1a1a] shrink-0 object-cover bg-white"
      style={{ width: size, height: size }}
    />
  );
}
